import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Button,
  Tabs,
  Tab,
  Divider,
  Chip
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';

const FAQPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(0);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleCategoryChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedCategory(newValue);
  };

  const categories = [
    'All',
    'Getting Started',
    'Account & Billing',
    'Features',
    'Security & Privacy',
    'Troubleshooting'
  ];

  const faqItems = [
    {
      category: 'Getting Started',
      question: 'What is Contract AI?',
      answer: 'Contract AI is an AI-powered platform for legal document generation, analysis, and management. It helps legal professionals and businesses create, review, and manage contracts more efficiently using advanced artificial intelligence technology.'
    },
    {
      category: 'Getting Started',
      question: 'How do I create my first document?',
      answer: 'To create your first document, log in to your account and click on the "New Document" button on your dashboard. You can either choose from our template library, upload an existing document, or generate a new one using AI by describing what you need.'
    },
    {
      category: 'Getting Started',
      question: 'What types of documents can I create with Contract AI?',
      answer: 'Contract AI supports a wide range of legal documents including NDAs, employment contracts, service agreements, sales contracts, lease agreements, and many more. Our template library is constantly expanding to cover more document types.'
    },
    {
      category: 'Account & Billing',
      question: 'How do I upgrade my subscription?',
      answer: 'To upgrade your subscription, go to your Account Settings and select the "Billing" tab. From there, you can choose a new plan and complete the upgrade process. Your new features will be available immediately after the upgrade is processed.'
    },
    {
      category: 'Account & Billing',
      question: 'Can I cancel my subscription at any time?',
      answer: 'Yes, you can cancel your subscription at any time from your Account Settings. If you cancel, you will continue to have access until the end of your current billing period. We don\'t offer refunds for partial billing periods.'
    },
    {
      category: 'Account & Billing',
      question: 'Do you offer discounts for non-profits or educational institutions?',
      answer: 'Yes, we offer special pricing for non-profit organizations, educational institutions, and students. Please contact our sales team at sales@contractai.com for more information about our discount programs.'
    },
    {
      category: 'Features',
      question: 'Can I export my documents?',
      answer: 'Yes, you can export your documents in various formats including PDF, DOCX, and TXT. To export a document, open it in the editor and click on the "Export" button in the top right corner. You can also choose to include or exclude comments and annotations.'
    },
    {
      category: 'Features',
      question: 'How does the AI risk analysis work?',
      answer: 'Our AI risk analysis feature uses machine learning algorithms trained on thousands of legal documents to identify potential risks and issues in your contracts. It scans for problematic clauses, missing terms, and inconsistencies, then provides suggestions for improvement.'
    },
    {
      category: 'Features',
      question: 'Can I collaborate with others on documents?',
      answer: 'Yes, Contract AI offers robust collaboration features. You can invite team members or external stakeholders to view or edit documents, track changes, add comments, and manage document versions. Access permissions can be set for each collaborator.'
    },
    {
      category: 'Security & Privacy',
      question: 'How secure is my data with Contract AI?',
      answer: 'Contract AI takes data security very seriously. We use industry-standard encryption for all data, both in transit and at rest. Our platform is compliant with major security standards including SOC 2, GDPR, and HIPAA. We also perform regular security audits and penetration testing.'
    },
    {
      category: 'Security & Privacy',
      question: 'Who has access to my documents?',
      answer: 'Only you and users you explicitly grant access to can view or edit your documents. Our staff does not access customer documents except in rare cases where you request support and grant permission. All access is logged and monitored for security purposes.'
    },
    {
      category: 'Security & Privacy',
      question: 'Can I delete my data permanently?',
      answer: 'Yes, you can permanently delete any document or your entire account at any time. When you delete data, it is immediately removed from our active systems and then securely erased from our backups within 30 days, in accordance with our data retention policy.'
    },
    {
      category: 'Troubleshooting',
      question: 'I forgot my password. How do I reset it?',
      answer: 'You can reset your password by clicking on the "Forgot Password" link on the login page. You will receive an email with instructions to reset your password. If you don\'t receive the email, please check your spam folder or contact support.'
    },
    {
      category: 'Troubleshooting',
      question: 'The document generation is taking too long. What should I do?',
      answer: 'Document generation usually takes just a few seconds, but complex documents may take longer. If it\'s taking more than a minute, try refreshing the page. If the problem persists, please contact our support team for assistance.'
    },
    {
      category: 'Troubleshooting',
      question: 'How do I report a bug or request a feature?',
      answer: 'You can report bugs or request features by going to the "Help" section in your account and clicking on "Submit Feedback". Alternatively, you can email support@contractai.com with details about the issue or feature request.'
    }
  ];

  const filteredFAQs = faqItems.filter(item => {
    // Filter by search query
    const matchesSearch = searchQuery === '' || 
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Filter by category
    const matchesCategory = selectedCategory === 0 || 
      item.category === categories[selectedCategory];
    
    return matchesSearch && matchesCategory;
  });

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ bgcolor: '#1a365d', color: 'white', py: 8, pt: 4 }}>
        {/* Header */}
        <LandingHeader />
        
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" fontWeight="bold" gutterBottom>
            Frequently Asked Questions
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: '700px', mb: 4, opacity: 0.9 }}>
            Find answers to common questions about Contract AI
          </Typography>
          
          <Box sx={{ maxWidth: '600px', mb: 4 }}>
            <TextField
              fullWidth
              placeholder="Search FAQs..."
              variant="outlined"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'white' }} />
                  </InputAdornment>
                ),
                sx: { 
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'white',
                  },
                  color: 'white'
                }
              }}
            />
          </Box>
        </Container>
      </Box>
      
      {/* FAQ Content */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
          <Tabs 
            value={selectedCategory} 
            onChange={handleCategoryChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {categories.map((category, index) => (
              <Tab 
                key={index} 
                label={category} 
                sx={{ fontWeight: 'medium' }}
              />
            ))}
          </Tabs>
        </Box>
        
        {filteredFAQs.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No results found
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We couldn't find any FAQs matching your search. Please try different keywords or browse by category.
            </Typography>
            {searchQuery && (
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => setSearchQuery('')}
                sx={{ mt: 3 }}
              >
                Clear Search
              </Button>
            )}
          </Box>
        ) : (
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Box>
                {filteredFAQs.map((item, index) => (
                  <Accordion 
                    key={index} 
                    sx={{ 
                      mb: 2, 
                      borderRadius: 1, 
                      overflow: 'hidden',
                      '&:before': {
                        display: 'none',
                      },
                      boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ 
                        bgcolor: 'rgba(0,0,0,0.02)',
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.04)',
                        }
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {item.question}
                        </Typography>
                        <Chip 
                          label={item.category} 
                          size="small" 
                          sx={{ 
                            mt: 1, 
                            bgcolor: '#e3f2fd', 
                            color: '#1976d2',
                            fontWeight: 500,
                            fontSize: '0.75rem'
                          }} 
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 3 }}>
                      <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                        {item.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box 
                sx={{ 
                  bgcolor: '#f8faff', 
                  p: 3, 
                  borderRadius: 2,
                  border: '1px solid rgba(0,0,0,0.05)',
                  position: 'sticky',
                  top: 24
                }}
              >
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Still Need Help?
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Can't find the answer you're looking for? Our support team is here to help.
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary"
                  fullWidth
                  href="/support"
                  sx={{ mb: 2 }}
                >
                  Contact Support
                </Button>
                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Popular Resources
                </Typography>
                <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      <a href="/documentation" style={{ color: '#1976d2', textDecoration: 'none' }}>
                        Documentation
                      </a>
                    </Typography>
                  </Box>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      <a href="/tutorials" style={{ color: '#1976d2', textDecoration: 'none' }}>
                        Video Tutorials
                      </a>
                    </Typography>
                  </Box>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      <a href="/community" style={{ color: '#1976d2', textDecoration: 'none' }}>
                        Community Forum
                      </a>
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography variant="body2">
                      <a href="/blog" style={{ color: '#1976d2', textDecoration: 'none' }}>
                        Blog
                      </a>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
      </Container>
      
      {/* Newsletter */}
      <Box sx={{ bgcolor: '#f8faff', py: 8 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', maxWidth: '600px', mx: 'auto' }}>
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Stay Updated
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
              Subscribe to our newsletter for the latest updates, tips, and resources.
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              gap: 2,
              maxWidth: '500px',
              mx: 'auto'
            }}>
              <Box 
                component="input" 
                type="email" 
                placeholder="Your email address"
                sx={{ 
                  flex: 1,
                  py: 1.5,
                  px: 2,
                  borderRadius: 2,
                  border: '1px solid #ddd',
                  fontSize: '1rem',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              />
              <Button 
                variant="contained" 
                color="primary"
                sx={{ 
                  py: 1.5, 
                  px: 3,
                  borderRadius: 2,
                  whiteSpace: 'nowrap'
                }}
              >
                Subscribe
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
      
      <Footer />
    </Box>
  );
};

export default FAQPage; 